<app-header></app-header>
<app-leftsidebar></app-leftsidebar>

<!-- Five drawn stars for a rating (full / half / empty). -->
<ng-template #starRow let-rating let-size="size">
  <span class="ld-stars" [class.ld-stars--lg]="size === 'lg'" aria-hidden="true">
    @for (star of stars(rating); track $index) {
      <svg viewBox="0 0 24 24" [class]="'ld-star ld-star--' + star">
        <defs>
          <linearGradient [attr.id]="'ld-half-' + $index">
            <stop offset="50%" stop-color="currentColor" />
            <stop offset="50%" stop-color="transparent" />
          </linearGradient>
        </defs>
        <polygon
          points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
          [attr.fill]="star === 'full' ? 'currentColor' : star === 'half' ? 'url(#ld-half-' + $index + ')' : 'none'"
        />
      </svg>
    }
  </span>
</ng-template>

<main class="listing-detail-shell">
<div class="listing-detail-page">
  @if (loading) {
    <!-- ===== Loading skeleton ===== -->
    <div class="ld-skel ld-skel--crumb"></div>
    <div class="ld-layout" aria-busy="true" aria-label="Loading item">
      <div class="ld-main">
        <div class="ld-skel ld-skel--hero"></div>
        <div class="ld-thumbs">
          @for (s of [1, 2, 3, 4]; track s) { <div class="ld-skel ld-skel--thumb"></div> }
        </div>
      </div>
      <aside class="ld-buy">
        <div class="ld-buy-card">
          <div class="ld-skel ld-skel--badge"></div>
          <div class="ld-skel ld-skel--title"></div>
          <div class="ld-skel ld-skel--line"></div>
          <div class="ld-skel ld-skel--price"></div>
          <div class="ld-skel ld-skel--btn"></div>
          <div class="ld-skel ld-skel--line"></div>
          <div class="ld-skel ld-skel--line is-short"></div>
        </div>
      </aside>
    </div>
  } @else if (!listing) {
    <div class="ld-empty">
      <span class="ld-empty-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="8" y1="11" x2="14" y2="11" /></svg>
      </span>
      <h3>Item not found</h3>
      <p>It may have been removed from the store.</p>
      <a routerLink="/store" class="wt-btn-primary">Back to the store</a>
    </div>
  } @else {
    <!-- ===== Breadcrumb ===== -->
    <nav class="ld-crumbs" aria-label="Breadcrumb">
      <a routerLink="/store">Store</a>
      @if (listing.category_detail) {
        <svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg>
        <a [routerLink]="['/store']" [queryParams]="{ type: listing.category_detail.name }">{{ listing.category_detail.name }}</a>
      }
      <svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg>
      <span aria-current="page">{{ listing.title }}</span>
    </nav>

    <div class="ld-layout">
      <!-- ===== Left: gallery + content ===== -->
      <div class="ld-main">
        <section class="ld-gallery" aria-label="Pictures">
          @if (images.length) {
            <button type="button" class="ld-hero" (click)="openViewer()" aria-label="View pictures full screen">
              <img [src]="images[activeImage]" [alt]="listing.title" />
              <span class="ld-hero-zoom" aria-hidden="true">
                <svg viewBox="0 0 24 24"><polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" /></svg>
              </span>
              @if (images.length > 1) {
                <span class="ld-hero-count">{{ activeImage + 1 }} / {{ images.length }}</span>
              }
            </button>
            @if (images.length > 1) {
              <div class="ld-thumbs">
                @for (src of images; track src; let i = $index) {
                  <button
                    type="button"
                    class="ld-thumb"
                    [class.is-active]="i === activeImage"
                    (click)="showImage(i)"
                    [attr.aria-label]="'Show picture ' + (i + 1)"
                    [attr.aria-pressed]="i === activeImage"
                  >
                    <img [src]="src" alt="" loading="lazy" />
                  </button>
                }
              </div>
            }
          } @else {
            <div class="ld-hero ld-hero--placeholder">
              <svg viewBox="0 0 24 24" aria-hidden="true"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" /><line x1="8" y1="2" x2="8" y2="18" /><line x1="16" y1="6" x2="16" y2="22" /></svg>
            </div>
          }
        </section>

        @if (listing.description) {
          <section class="ld-section">
            <h2>Description</h2>
            <p class="ld-description">{{ listing.description }}</p>
          </section>
        }

        @if (specRows.length) {
          <section class="ld-section">
            <h2>Details</h2>
            <dl class="ld-specs">
              @for (row of specRows; track row.label) {
                <div class="ld-spec">
                  <dt>{{ row.label }}</dt>
                  <dd>{{ row.value }}</dd>
                </div>
              }
            </dl>
          </section>
        }

        <!-- ===== Reviews ===== -->
        <section class="ld-section" id="reviews">
          <h2>Reviews</h2>

          <div class="ld-review-summary">
            @if (listing.rating_count) {
              <span class="ld-review-avg">{{ ratingAverage | number: '1.1-1' }}</span>
              <div>
                <ng-container *ngTemplateOutlet="starRow; context: { $implicit: ratingAverage, size: 'lg' }"></ng-container>
                <span class="ld-review-count">{{ listing.rating_count }} review{{ listing.rating_count === 1 ? '' : 's' }}</span>
              </div>
            } @else {
              <span class="ld-review-none">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                No reviews yet{{ listing.can_review ? ' — be the first.' : '.' }}
              </span>
            }
          </div>

          <!-- Only owners who haven't reviewed it yet; the API checks the same. -->
          @if (listing.can_review) {
            <form class="review-form" (ngSubmit)="submitReview()">
              <span class="review-form-label">Your rating</span>
              <div class="review-form-stars" role="group" aria-label="Your rating">
                @for (star of starChoices; track star) {
                  <button
                    type="button"
                    class="review-form-star"
                    [class.is-on]="star <= draftRating"
                    [attr.aria-label]="star + (star === 1 ? ' star' : ' stars')"
                    [attr.aria-pressed]="star <= draftRating"
                    (click)="setRating(star)"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
                  </button>
                }
              </div>
              <textarea
                class="review-form-body"
                rows="3"
                placeholder="Share how it played at your table (optional)"
                [(ngModel)]="draftBody"
                name="reviewBody"
              ></textarea>
              <button
                type="submit"
                class="wt-btn-primary review-form-submit"
                [disabled]="submittingReview || draftRating < 1"
              >{{ submittingReview ? 'Posting…' : 'Post review' }}</button>
            </form>
          }

          @for (review of reviews; track review.id) {
            <article class="ld-review">
              <div class="ld-review-head">
                <span class="ld-review-avatar" aria-hidden="true">{{ (review.reviewer_display || 'A').charAt(0).toUpperCase() }}</span>
                <div class="ld-review-who">
                  <span class="ld-review-author">{{ review.reviewer_display }}</span>
                  <span class="ld-review-date">{{ review.created_at | date: 'MMM d, y' }}</span>
                </div>
                <ng-container *ngTemplateOutlet="starRow; context: { $implicit: review.rating }"></ng-container>
              </div>
              @if (review.body) {
                <p class="ld-review-body">{{ review.body }}</p>
              }
              @if (review.vendor_response) {
                <div class="ld-review-response">
                  <strong>Reply from {{ listing.vendor.shop_name }}</strong>
                  <p>{{ review.vendor_response }}</p>
                </div>
              }
            </article>
          }
        </section>
      </div>

      <!-- ===== Right: buy panel ===== -->
      <aside class="ld-buy">
        <div class="ld-buy-card">
          @if (listing.category_detail) {
            <span class="ld-type">{{ listing.category_detail.name }}</span>
          }
          <h1>{{ listing.title }}</h1>

          <a class="ld-shop" [routerLink]="['/store']" [queryParams]="{ author: listing.vendor.shop_slug }">
            <span class="ld-shop-avatar" aria-hidden="true">{{ listing.vendor.shop_name.charAt(0).toUpperCase() }}</span>
            <span>by <strong>{{ listing.vendor.shop_name }}</strong></span>
          </a>

          <div class="ld-rating">
            @if (listing.rating_count) {
              <ng-container *ngTemplateOutlet="starRow; context: { $implicit: ratingAverage }"></ng-container>
              <span>{{ ratingAverage | number: '1.1-1' }} · {{ listing.rating_count }} review{{ listing.rating_count === 1 ? '' : 's' }}</span>
            } @else {
              <span class="ld-muted">No reviews yet</span>
            }
          </div>

          <div class="ld-price" [class.is-free]="isFree">{{ price(listing) }}</div>

          @if (listing.short_description) {
            <p class="ld-short">{{ listing.short_description }}</p>
          }

          <div class="ld-actions">
            @if (listing.is_mine) {
              <div class="ld-note">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                This is your item — buyers see a buy button here.
              </div>
            } @else if (listing.is_owned) {
              <div class="ld-note ld-note--owned">
                <svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                You own this — it's in your Content Vault.
              </div>
              <a class="wt-btn-primary ld-main-btn" routerLink="/marketplace/orders">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                Go to downloads
              </a>
            } @else if (isFree) {
              <button type="button" class="wt-btn-primary ld-main-btn" [disabled]="claiming" (click)="claimFree()">
                <svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="20 12 20 22 4 22 4 12" /><rect x="2" y="7" width="20" height="5" /><line x1="12" y1="22" x2="12" y2="7" /><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" /><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" /></svg>
                {{ claiming ? 'Getting…' : 'Get for free' }}
              </button>
            } @else {
              <!-- A digital item is owned once - more copies only repeat the same downloads. -->
              @if (listing.fulfillment_type !== 'digital') {
                <div class="quantity-control">
                  <button type="button" (click)="quantity = quantity > 1 ? quantity - 1 : 1" aria-label="Fewer">&minus;</button>
                  <span>{{ quantity }}</span>
                  <button type="button" (click)="quantity = quantity + 1" aria-label="More">+</button>
                </div>
              }
              <button type="button" class="wt-btn-primary ld-main-btn" [disabled]="addingToCart" (click)="addToCart()">
                <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" /></svg>
                {{ addingToCart ? 'Adding…' : 'Add to cart' }}
              </button>
            }

            @if (!listing.is_mine) {
              <button
                type="button"
                class="ld-fav"
                [class.is-on]="isFavorited"
                (click)="toggleFavorite()"
                [attr.aria-pressed]="isFavorited"
                [attr.aria-label]="isFavorited ? 'Remove from favorites' : 'Add to favorites'"
                [title]="isFavorited ? 'Remove from favorites' : 'Add to favorites'"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>
              </button>
            }
          </div>

          @if (addedToCart) {
            <button type="button" class="ld-cart-link" (click)="goToCart()">
              View cart & check out
              <svg viewBox="0 0 24 24" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
            </button>
          }

          @if (listing.ai_generated) {
            <p class="ld-ai">
              AI-assisted content @if (listing.ai_disclosure_percent != null) { (~{{ listing.ai_disclosure_percent }}%) }
            </p>
          }

          <!-- ===== What's included ===== -->
          <div class="ld-included">
            <h3>What's included</h3>
            <ul>
              @for (row of includedFiles; track row.kind) {
                <li>
                  <span [class]="'ld-inc-icon ld-inc-icon--' + row.kind" aria-hidden="true">
                    @switch (row.kind) {
                      @case ('image') { <svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg> }
                      @case ('doc') { <svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="13" y2="17" /></svg> }
                      @default { <svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><polyline points="14 2 14 8 20 8" /></svg> }
                    }
                  </span>
                  {{ row.label }}
                </li>
              }
              @if (listing.fulfillment_type === 'digital') {
                <li>
                  <span class="ld-inc-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg></span>
                  Instant download, re-download any time
                </li>
              }
              @if (licenseLabel) {
                <li>
                  <span class="ld-inc-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg></span>
                  {{ licenseLabel }}
                </li>
              }
            </ul>
          </div>

          @if (!listing.is_mine) {
            <button type="button" class="ld-report-link" (click)="openReport()">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" y1="22" x2="4" y2="15" /></svg>
              Report this item
            </button>
          }
        </div>
      </aside>
    </div>

    <!-- ===== More from this shop ===== -->
    @if (moreFromShop.length) {
      <section class="ld-more">
        <div class="ld-more-head">
          <h2>More from {{ listing.vendor.shop_name }}</h2>
          <a [routerLink]="['/store']" [queryParams]="{ author: listing.vendor.shop_slug }">
            See all
            <svg viewBox="0 0 24 24" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
          </a>
        </div>
        <div class="ld-more-grid">
          @for (item of moreFromShop; track item.id) {
            <a class="ld-more-card" [routerLink]="['/marketplace/listing', item.id]">
              <span class="ld-more-cover">
                @if (item.cover_image) {
                  <img [src]="item.cover_image" alt="" loading="lazy" />
                }
                @if (item.category_detail) {
                  <span class="ld-more-type">{{ item.category_detail.name }}</span>
                }
              </span>
              <span class="ld-more-body">
                <span class="ld-more-title">{{ item.title }}</span>
                <span class="ld-more-price" [class.is-free]="!(+item.price)">{{ price(item) }}</span>
              </span>
            </a>
          }
        </div>
      </section>
    }
  }
</div>
</main>

<!-- ===== Report this item ===== -->
@if (reportOpen && listing) {
  <div class="ld-modal-backdrop" (click)="closeReport()">
    <div class="ld-modal" role="dialog" aria-modal="true" aria-labelledby="ld-report-title" (click)="$event.stopPropagation()">
      <button type="button" class="ld-modal-close" (click)="closeReport()" aria-label="Close">
        <svg viewBox="0 0 24 24" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
      </button>

      @if (reportSent) {
        <div class="ld-report-done">
          <span class="ld-report-done-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg></span>
          <h2 id="ld-report-title">Thanks for letting us know</h2>
          <p>Our team will look at “{{ listing.title }}” and take action if it breaks the store rules.</p>
          <button type="button" class="wt-btn-primary" (click)="closeReport()">Done</button>
        </div>
      } @else {
        <h2 id="ld-report-title">Report this item</h2>
        <p class="ld-modal-intro">What's wrong with “{{ listing.title }}”? Reports go to the WorldTumbler team, not the seller.</p>

        <div class="ld-reasons" role="radiogroup" aria-label="Reason">
          @for (reason of reportReasons; track reason.value) {
            <label class="ld-reason" [class.is-on]="reportReason === reason.value">
              <input type="radio" name="reportReason" [value]="reason.value" [(ngModel)]="reportReason" />
              <span class="ld-reason-text">
                <strong>{{ reason.label }}</strong>
                <span>{{ reason.hint }}</span>
              </span>
            </label>
          }
        </div>

        <label class="ld-report-details">
          <span>Details {{ reportReason === 'other' ? '(required)' : '(optional)' }}</span>
          <textarea rows="3" maxlength="2000" [(ngModel)]="reportDetails" name="reportDetails"
            placeholder="Anything that helps us check it — e.g. where the original art comes from."></textarea>
        </label>

        <div class="ld-modal-actions">
          <button type="button" class="ld-modal-cancel" (click)="closeReport()">Cancel</button>
          <button type="button" class="wt-btn-primary" [disabled]="!reportReady || reporting" (click)="submitReport()">
            {{ reporting ? 'Sending…' : 'Send report' }}
          </button>
        </div>
      }
    </div>
  </div>
}

<!-- ===== Full-screen picture viewer ===== -->
@if (viewerOpen && listing) {
  <div class="ld-viewer" role="dialog" aria-modal="true" aria-label="Pictures" (click)="closeViewer()">
    <button type="button" class="ld-viewer-close" (click)="closeViewer()" aria-label="Close">
      <svg viewBox="0 0 24 24" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
    </button>
    @if (images.length > 1) {
      <button type="button" class="ld-viewer-nav ld-viewer-nav--prev" (click)="showImage(activeImage - 1); $event.stopPropagation()" aria-label="Previous picture">
        <svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="15 18 9 12 15 6" /></svg>
      </button>
    }
    <img class="ld-viewer-img" [src]="images[activeImage]" [alt]="listing.title" (click)="$event.stopPropagation()" />
    @if (images.length > 1) {
      <button type="button" class="ld-viewer-nav ld-viewer-nav--next" (click)="showImage(activeImage + 1); $event.stopPropagation()" aria-label="Next picture">
        <svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg>
      </button>
      <span class="ld-viewer-count">{{ activeImage + 1 }} / {{ images.length }}</span>
    }
  </div>
}
<app-footer></app-footer>
